import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ChevronRight, Shield, Truck, Award } from "lucide-react";
import axios from "axios";

const API = `/api`;

const HomePage = () => {
  const [groups, setGroups] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/groups`),
          axios.get(`${API}/reviews/approved`)
        ]);
        setGroups(groupsRes.data);
        setReviews(reviewsRes.data);
      } catch {
        // Error fetching data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const usps = [
    {
      icon: Award,
      title: "Quality Assured",
      description: "Strict quality control on every meter of fabric we supply"
    },
    {
      icon: Shield,
      title: "Trust & Reliability",
      description: "Building lasting partnerships with transparent dealings"
    },
    {
      icon: Truck,
      title: "Stable Supply",
      description: "Consistent availability for our regular customers"
    }
  ];

  return (
    <div data-testid="home-page">
      <Helmet>
        <title>DUGARS | Premium Fabric Trading — Reliable Wholesale Fabrics</title>
        <meta name="description" content="DUGARS delivers quality fabrics for modern garment businesses. Reliable wholesale fabric trading with 5+ collections and 200+ happy clients." />
        <link rel="canonical" href="https://thedugars.in/" />
        <meta property="og:title" content="DUGARS | Premium Fabric Trading" />
        <meta property="og:description" content="Your trusted partner for affordable, quality-assured wholesale fabrics." />
        <meta property="og:url" content="https://thedugars.in/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://thedugars.in/api/images/hero_homepage.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DUGARS | Premium Fabric Trading" />
        <meta name="twitter:description" content="Your trusted partner for affordable, quality-assured wholesale fabrics." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "DUGARS",
          "description": "Premium fabric trading — reliable wholesale fabrics for garment businesses",
          "url": "https://thedugars.in",
          "telephone": "+916360372623",
          "email": "info@thedugars.in",
          "address": { "@type": "PostalAddress", "streetAddress": "26, Jagat Banerjee Ghat Rd, Choura Bustee, Shibpur", "addressLocality": "Howrah", "addressRegion": "West Bengal", "postalCode": "711102", "addressCountry": "IN" },
          "openingHours": ["Mo-Fr 09:00-18:00", "Sa 10:00-16:00"]
        })}</script>
      </Helmet>
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center"
        data-testid="hero-section"
      >
        {/* Background Image - Warehouse with fabric rolls */}
        <div className="absolute inset-0 z-0">
          <img
            src="/api/images/hero_homepage.png"
            alt="DUGARS fabric warehouse with rolls stacked"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1A]/85 via-[#1C1C1A]/60 to-transparent" />
        </div>

        {/* Hero Content - Left Aligned */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="max-w-2xl">
            <div className="animate-fadeInUp">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.3em] text-[#F7F5F0]/70 mb-6">
                Your Dependable Partner in Fabric Trading
              </span>
            </div>
            
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-tight text-[#F7F5F0] mb-8 animate-fadeInUp animate-delay-100">
              Premium Fabrics,<br />
              <span className="italic">Trusted Sourcing,</span><br />
              Seamless Supply
            </h1>
            
            <p className="text-lg md:text-xl text-[#F7F5F0]/80 font-light mb-12 animate-fadeInUp animate-delay-200">
              DUGARS delivers quality materials tailored for modern garment businesses. 
              With a focus on reliability, variety, and fast turnaround, we are your dependable partner in fabric trading.
            </p>
          
            <div className="flex flex-col sm:flex-row items-start gap-4 animate-fadeInUp animate-delay-300">
              <Link
                to="/products"
                className="btn-primary flex items-center gap-2"
                data-testid="hero-explore-btn"
              >
                Explore Collections
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/become-partner"
                className="bg-white text-[#1C1C1A] px-8 py-4 text-sm font-medium uppercase tracking-wide hover:bg-[#F7F5F0] transition-colors"
                data-testid="hero-partner-btn"
              >
                Become a Regular Customer
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#F7F5F0]/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-[#F7F5F0]/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="usp-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="sr-only">Why Choose DUGARS</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {usps.map((usp) => (
              <div 
                key={usp.title} 
                className="text-center"
                data-testid={`usp-${usp.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#F7F5F0] border border-[#E6E4DD]">
                  <usp.icon size={28} className="text-[#4A5D4E]" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1C1C1A] mb-3">
                  {usp.title}
                </h3>
                <p className="text-[#6B6A65] font-light text-sm leading-relaxed">
                  {usp.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collections Section */}
      <section className="py-24 md:py-32" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">
              Our Collections
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#1C1C1A]">
              Explore Our Range
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-[#E6E4DD] mb-4" />
                  <div className="h-6 bg-[#E6E4DD] w-3/4 mb-2" />
                  <div className="h-4 bg-[#E6E4DD] w-1/2" />
                </div>
              ))}
            </div>
          ) : groups.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {groups.slice(0, 6).map((group) => (
                <Link
                  key={group.id}
                  to={`/products/${group.id}`}
                  className="group block"
                  data-testid={`category-link-${group.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#1C1C1A]">
                    {group.image_url ? (
                      <img src={group.image_url} alt={group.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                    ) : null}
                    <div className={`w-full h-full items-center justify-center bg-gradient-to-br from-[#1C1C1A] to-[#4A5D4E] ${group.image_url ? 'hidden' : 'flex'}`}>
                      <span className="font-['Cormorant_Garamond'] text-2xl text-[#F7F5F0]/30 text-center px-6">{group.name}</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1A]/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#F7F5F0] mb-1">{group.name}</h3>
                      {group.variant_count > 0 && <p className="text-[#F7F5F0]/60 text-sm">{group.variant_count} Colours Available</p>}
                      <span className="inline-flex items-center gap-2 mt-3 text-sm font-medium uppercase tracking-wide text-[#F7F5F0] group-hover:gap-3 transition-all">
                        View Collection <ChevronRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#6B6A65]">Collections coming soon.</p>
              <Link to="/products" className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-[#4A5D4E]">
                View All Products <ArrowRight size={16} />
              </Link>
            </div>
          )}

          {groups.length > 3 && (
            <div className="text-center mt-12">
              <Link to="/products" className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-[#4A5D4E] hover:gap-3 transition-all link-underline">
                View All Collections <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </div>
      </section>


      {/* Customer Reviews Section */}
      {reviews.length > 0 && (
        <section className="py-24 md:py-32" data-testid="reviews-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">
                Testimonials
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#1C1C1A]">
                What Our Customers Say
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {reviews.slice(0, 3).map((review) => (
                <div 
                  key={review.id} 
                  className="bg-white p-8 border border-[#E6E4DD]"
                  data-testid={`review-card-${review.id}`}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${i < review.rating ? 'text-[#4A5D4E]' : 'text-[#E6E4DD]'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  
                  <p className="text-[#6B6A65] font-light leading-relaxed mb-6">
                    "{review.review_text}"
                  </p>
                  
                  <div>
                    <p className="font-medium text-[#1C1C1A]">{review.customer_name}</p>
                    {review.company && (
                      <p className="text-sm text-[#6B6A65]">{review.company}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/reviews"
                className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-[#4A5D4E] hover:gap-3 transition-all link-underline"
                data-testid="view-all-reviews-link"
              >
                View All Reviews
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* About Teaser Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-teaser-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
            {/* Image */}
            <div className="md:col-span-5 order-2 md:order-1">
              <div className="aspect-[4/5] overflow-hidden image-hover-zoom">
                <img
                  src="/api/images/about_section.png"
                  alt="About DUGARS"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content */}
            <div className="md:col-span-7 order-1 md:order-2 md:pl-8">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-6 block">
                About DUGARS
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#1C1C1A] leading-tight mb-8">
                Affordable Quality,<br />
                Reliable Supply
              </h2>
              <p className="text-[#6B6A65] font-light leading-relaxed mb-8">
                DUGARS has established itself as a trusted name in the fabric trading industry. 
                We specialize in sourcing and supplying affordable, quality-assured imported fabrics. 
                Our strict quality control and commitment to stable supply has made us the preferred 
                partner for manufacturers and designers who value reliability.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-[#4A5D4E] hover:gap-3 transition-all link-underline"
                data-testid="learn-more-link"
              >
                Learn More About Us
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1C1C1A]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-[#F7F5F0] mb-6">
            Ready to Partner With Us?
          </h2>
          <p className="text-[#F7F5F0]/70 font-light leading-relaxed mb-10 max-w-2xl mx-auto">
            Contact us today to discuss your fabric requirements. Enjoy stable supply, 
            strict quality control, and competitive pricing for your business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/inquiry"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="cta-inquiry-btn"
            >
              Send Inquiry
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="bg-white text-[#1C1C1A] px-8 py-4 text-sm font-medium uppercase tracking-wide hover:bg-[#F7F5F0] transition-colors inline-flex items-center gap-2"
              data-testid="cta-contact-btn"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
